import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import useSourceFromQuery from '../../hooks/useSourceFromQuery';
import { useSubscription } from '../../hooks/useSubscription';

import { useIsCheckingAuth } from '../../contexts/AuthContext';
import { useBrand, useDefaultBrand, useSite, useSubscriptions } from '../../contexts/SiteContext';
import BasicButton from '../buttons/BasicButton';
import BrandLogoButton from '../buttons/BrandLogoButton';
import SubscriptionForm from '../forms/SubscriptionForm';
import ConspiIcon from '../icons/ConspiIcon';
import FreeIcon from '../icons/FreeIcon';
import Video from '../partials/Video';
import PromotionDescription from '../typography/PromotionDescription';
import PromotionTitle from '../typography/PromotionTitle';

import styles from '../../styles/pages/subscription-page.module.css';

export interface SubscriptionPageProps {
    brand?: string | null;
    disabled?: boolean;
    current?: boolean;
    subscribe?: boolean;
    friend?: boolean;
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
    className?: string | null;
    contentClassName?: string | null;
}
function SubscriptionPage({
    brand: brandSlug = null,
    disabled = false,
    subscribe: shouldSubscribe = false,
    friend: subscribeFriend = false,
    current = false,
    entered = false,
    entering = false,
    leaving = false,
    onEnter = null,
    onLeave = null,
    className = null,
    contentClassName = null,
}: SubscriptionPageProps) {
    const url = useUrlGenerator();

    const { handle: siteHandle = null } = useSite();
    const withFriend = siteHandle === 'urbania';
    const hasBrand = brandSlug !== null;
    const { handle: defaultBrandHandle = 'urbania' } = useDefaultBrand();
    const brand = useBrand(brandSlug, !hasBrand ? defaultBrandHandle : null);
    const {
        handle: brandHandle = null,
        label: brandLabel,
        default: isDefault = false,
    } = brand || {};
    const sourceQuery = useSourceFromQuery();
    const subscriptions = useSubscriptions();
    const subscription =
        subscriptions.find(
            ({ handle: subscriptionHandle }) => subscriptionHandle === brandHandle,
        ) || null;
    const isCheckingAuth = useIsCheckingAuth();
    const user = useUser();
    const { description } = subscription;
    const { subscribed: userSubscribed = false, subscribe } = useSubscription({
        subscription: brandHandle,
        source: sourceQuery || 'link',
    });

    const [friendSubscribed, setFriendSubscribed] = useState(false);
    const subscribed = subscribeFriend ? friendSubscribed : userSubscribed;

    const transitions = useTransition(subscribeFriend ? 'friend' : 'me', {
        initial: { x: '0%' },
        from: (item) => ({ x: item === 'friend' ? '100%' : '-100%' }),
        enter: { x: '0%' },
        leave: (item) => ({ x: item === 'friend' ? '100%' : '-100%' }),
        keys: (item) => item,
    });

    const onComplete = () => {
        if (subscribeFriend) {
            // setFriendSubscribed(true);
        }
    };

    // useScrollEnter({
    //     disabled: !current,
    //     onEnter,
    //     onLeave,
    // });

    useEffect(() => {
        if (shouldSubscribe && current && user !== null && !subscribed && subscription !== null) {
            subscribe();
        }
    }, [shouldSubscribe, isCheckingAuth]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.entering]: entering,
                    [styles.leaving]: leaving,
                    [styles.friend]: subscribeFriend,
                },
                className,
                contentClassName,
            ])}
            data-theme={brandHandle === 'urbania' ? 'yellow' : null}
            data-brand={brandHandle}
        >
            <BrandLogoButton brand={brand} theme="light" className={styles.urbania} />
            {withFriend ? (
                <div className={styles.top}>
                    <div
                        className={classNames([
                            styles.tabs,
                            {
                                [styles.on]: subscribeFriend,
                            },
                        ])}
                    >
                        <BasicButton
                            href={url('subscription', {
                                brand: brandSlug,
                            })}
                            className={classNames([
                                styles.tab,
                                {
                                    [styles.current]: !subscribeFriend,
                                },
                            ])}
                        >
                            S’abonner
                        </BasicButton>
                        <BasicButton
                            href={url('subscription.friend', {
                                brand: brandSlug,
                            })}
                            className={classNames([
                                styles.tab,
                                {
                                    [styles.current]: subscribeFriend,
                                },
                            ])}
                        >
                            Abonner un proche
                        </BasicButton>
                    </div>
                    <ConspiIcon className={styles.conspiIcon} />
                </div>
            ) : null}
            {transitions((style, item) => (
                <animated.div
                    style={style}
                    className={classNames([
                        styles.page,
                        {
                            [styles.leaving]:
                                (item === 'friend' && !subscribeFriend) ||
                                (item !== 'friend' && subscribeFriend),
                        },
                    ])}
                >
                    <div className={styles.inner}>
                        <div className={styles.header}>
                            <PromotionTitle className={styles.title}>
                                {subscribed && item === 'friend' ? (
                                    <FormattedMessage
                                        defaultMessage="Vous avez abonné à {brand}!"
                                        description="Page title"
                                        values={{
                                            brand:
                                                brandHandle === 'france'
                                                    ? 'la newsletter URBANIA'
                                                    : brandLabel,
                                        }}
                                    />
                                ) : subscribed ? (
                                    <FormattedMessage
                                        defaultMessage="Vous êtes abonné·e à {brand}!"
                                        description="Page title"
                                        values={{
                                            brand:
                                                brandHandle === 'france'
                                                    ? 'la newsletter URBANIA'
                                                    : brandLabel,
                                        }}
                                    />
                                ) : item === 'friend' ? (
                                    <FormattedMessage
                                        defaultMessage="Abonnez secrètement un proche à {brand}!"
                                        description="Page title"
                                        values={{
                                            brand:
                                                brandHandle === 'france'
                                                    ? 'la newsletter URBANIA'
                                                    : brandLabel,
                                        }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Abonnez-vous à {brand}!"
                                        description="Page title"
                                        values={{
                                            brand:
                                                brandHandle === 'france'
                                                    ? 'la newsletter URBANIA'
                                                    : brandLabel,
                                        }}
                                    />
                                )}
                                {isDefault ? <FreeIcon className={styles.free} /> : null}
                            </PromotionTitle>
                            {item === 'friend' ? (
                                <PromotionDescription className={styles.description}>
                                    <p>
                                        Un cousin alpha, une collègue bornée, une tante conspi, un
                                        oncle MAGA, une grand-mère grossophobe?
                                    </p>
                                    <p>
                                        URBANIA souhaite ouvrir les esprits et vous invite à les
                                        amener ailleurs.
                                    </p>
                                </PromotionDescription>
                            ) : brandHandle === 'urbania' && !subscribed && item !== 'friend' ? (
                                <PromotionDescription className={styles.description}>
                                    Entrez votre courriel ou votre numéro de téléphone pour accéder
                                    à votre profil, profiter de rabais exclusifs chez nos
                                    partenaires, consulter votre historique et gérer vos
                                    abonnements.
                                </PromotionDescription>
                            ) : brandHandle === 'france' ? (
                                <PromotionDescription className={styles.description}>
                                    <p>
                                        Des infos, des blagues réussies et plein de surprises
                                        dénichées rien que pour vous.
                                    </p>
                                    <ul>
                                        <li>Lectorat 100% satisfait (selon une source anonyme)</li>
                                        <li>
                                            si vous ne nous croyez pas, jetez un oeil{' '}
                                            <Link href={url('newsletters')}>
                                                aux newsletters passées
                                            </Link>
                                        </li>
                                    </ul>
                                </PromotionDescription>
                            ) : (
                                <PromotionDescription
                                    className={styles.description}
                                    html={description}
                                />
                            )}
                        </div>
                        <div className={styles.form}>
                            {!isCheckingAuth || user !== null ? (
                                <SubscriptionForm
                                    subscription={brandHandle}
                                    via={
                                        brandHandle === 'urbania' || brandHandle === 'france'
                                            ? ['sms', 'email']
                                            : null
                                    }
                                    full={item !== 'friend'}
                                    source={sourceQuery || 'page'}
                                    ssoSize="large"
                                    withoutButtons
                                    // alwaysVertical={subscribeFriend}
                                    friend={item === 'friend'}
                                    className={styles.form}
                                    fieldsClassName={styles.fields}
                                    placeholder={
                                        item === 'friend'
                                            ? 'Entrez son courriel ou # de tél.'
                                            : null
                                    }
                                    subscribeButtonLabel={item === 'friend' ? 'Abonner' : null}
                                    subscriptionsClassName={styles.subscriptions}
                                    ssoClassName={styles.sso}
                                    onComplete={onComplete}
                                />
                            ) : (
                                <div className={styles.placeholder}>Chargement...</div>
                            )}
                        </div>
                        {item === 'friend' ? (
                            <>
                                <PromotionDescription className={styles.disclaimer}>
                                    Entrez le courriel ou le numéro de téléphone de la personne que
                                    vous souhaitez abonner à {brandLabel} et elle recevra un message
                                    avec les instructions pour compléter son abonnement.
                                </PromotionDescription>
                            </>
                        ) : null}
                    </div>
                    {item === 'friend' ? (
                        <>
                            {siteHandle !== 'france' ? (
                                <div className={styles.videos}>
                                    <iframe
                                        className={styles.video}
                                        width="560"
                                        height="315"
                                        src="https://www.youtube.com/embed/RwLA1uV5oTI?si=seGliRn-Yf9AEOkf"
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                    ></iframe>
                                    <iframe
                                        className={styles.video}
                                        width="560"
                                        height="315"
                                        src="https://www.youtube.com/embed/8VpBndJcWAo?si=-goImlqq5vX0U58i"
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                    ></iframe>
                                    <iframe
                                        className={styles.video}
                                        width="560"
                                        height="315"
                                        src="https://www.youtube.com/embed/mYBKGBWw8lI?si=DyHYU5hIfnkHSmVf"
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </animated.div>
            ))}
        </div>
    );
}

export default SubscriptionPage;
